import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, MessagePlugin, Popconfirm, Select, Space, Table, Tag } from 'tdesign-react';
import type { PageInfo, PrimaryTableCol } from 'tdesign-react';
import { AddIcon, BrowseIcon, RefreshIcon, SendIcon } from 'tdesign-icons-react';
import {
  DISPATCH_STATUS_LABEL,
  DISPATCH_STATUS_THEME,
  DispatchStatus,
  SUB_CATEGORY_LABEL,
  SUB_CATEGORY_OPTIONS,
  createCommand,
  fetchCommandList,
  redispatchCommand,
  type CommandRecord,
  type CreateCommandPayload,
} from '@/services/command';
import { formatDateTime, formatDateOnly } from '@/utils/datetime';
import CommandFormDialog from './CommandFormDialog';
import './CommandList.css';

const CommandList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<CommandRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [subCategory, setSubCategory] = useState<number | undefined>();

  const [dialogVisible, setDialogVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      // commandType 字段仍保留在后端 DTO 里（DB 字段未改），但前端页面已下线该维度，
      // 因此这里不再传 commandType，交给后端"不过滤"处理。
      const res = await fetchCommandList({ page, pageSize, subCategory });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, subCategory]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleResetFilters = () => {
    setSubCategory(undefined);
    setPage(1);
    // 即使过滤条件没有发生变化（loadList 依赖未变 → useEffect 不会再触发），
    // 也强制刷新一次，保证用户点击「重置」始终有反馈。
    loadList();
  };

  const handleOpenCreate = () => setDialogVisible(true);

  /**
   * 跳转到「指令详情」页。
   *
   * 只带 id，不再通过 location.state 携带列表快照。
   * 指令的下发/完成状态和回执附件都可能实时变化，详情页每次挂载都会重新走
   * `/command/detail?id=xxx` 拉最新数据，避免展示到过期的列表快照。
   */
  const handleOpenDetail = (record: CommandRecord) => {
    navigate(`/commands/detail?id=${record.id}`);
  };

  const handleRedispatch = async (record: CommandRecord) => {
    try {
      await redispatchCommand(record.id);
      MessagePlugin.success(`已再次下发`);
    } catch {
      /* 错误提示由拦截器统一处理 */
    }
  };

  const handleSubmit = async (payload: CreateCommandPayload) => {
    setSubmitting(true);
    try {
      await createCommand(payload);
      MessagePlugin.success('新增成功');
      setDialogVisible(false);
      setPage(1);
      loadList();
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo<PrimaryTableCol<CommandRecord>[]>(
    () => [
      { colKey: 'id', title: 'ID', width: 80 },
      {
        colKey: 'subCategory',
        title: '指令分类',
        width: 160,
        cell: ({ row }) => SUB_CATEGORY_LABEL[row.subCategory] ?? row.subCategory,
      },
      {
        colKey: 'needReply',
        title: '是否需反馈',
        width: 110,
        cell: ({ row }) =>
          row.needReply === 1 ? (
            <Tag theme="warning" variant="light">
              需反馈
            </Tag>
          ) : (
            <Tag variant="light">无需反馈</Tag>
          ),
      },
      {
        colKey: 'deadlineDate',
        title: '反馈日期',
        width: 150,
        cell: ({ row }) => {
          if (row.needReply !== 1 || !row.deadlineDate) return '-';
          // 展示统一走 formatDateOnly，保证与「下发时间」格式风格一致（YYYY/M/D）。
          const label = formatDateOnly(row.deadlineDate);
          // 仅在指令未完成时高亮"今日到期/已逾期"。已完成的指令过截止日不再警示。
          if (row.dispatchStatus === DispatchStatus.Completed) {
            return label;
          }
          // 逾期/今日到期判断使用原始 YYYY-MM-DD（字典序即时间序），比对更稳定。
          const today = new Date().toISOString().slice(0, 10);
          if (row.deadlineDate < today) {
            return (
              <Tag theme="danger" variant="light">
                {label} 已逾期
              </Tag>
            );
          }
          if (row.deadlineDate === today) {
            return (
              <Tag theme="warning" variant="light">
                {label} 今日到期
              </Tag>
            );
          }
          return label;
        },
      },
      {
        colKey: 'dispatchStatus',
        title: '状态',
        width: 110,
        cell: ({ row }) => (
          <Tag theme={DISPATCH_STATUS_THEME[row.dispatchStatus] ?? 'default'} variant="light">
            {DISPATCH_STATUS_LABEL[row.dispatchStatus] ?? row.dispatchStatus}
          </Tag>
        ),
      },

      {
        colKey: 'submitter',
        title: '提交人',
        width: 200,
        // 展示回退链：submitterName（门户微信登录 → t_account.name 反查）→ createdBy（后台管理入口的 staffname）→ submitter 原值。
        // 后台管理入口不再冗余写 submitter，所以其记录只有 createdBy 有值。
        // 如果反查到了提交人的 organization（仅微信登录路径会有），在姓名后以「（单位）」形式追加展示；
        // 未反查到（如太湖 staffname / 老账号未填 organization）不显示括号，避免"张三（未知）"。
        cell: ({ row }) => {
          const name = row.submitterName || row.createdBy || row.submitter || '-';
          if (name === '-' || !row.submitterOrganization) return name;
          return `${name}（${row.submitterOrganization}）`;
        },
      },
      {
        colKey: 'createdAt',
        title: '提交时间',
        width: 180,
        cell: ({ row }) => formatDateTime(row.createdAt),
      },
      {
        colKey: 'dispatchedBy',
        title: '下发人',
        width: 140,
        // dispatched_by 存的是企微 userid（或历史兜底值 'wecom-click'）。
        // 目前后端 list 没有反查通讯录，这里直接展示原值即可；未下发时为 null。
        cell: ({ row }) => row.dispatchedBy || '-',
      },
      {
        colKey: 'dispatchedAt',
        title: '下发时间',
        width: 180,
        cell: ({ row }) => (row.dispatchedAt ? formatDateTime(row.dispatchedAt) : '-'),
      },
      {
        colKey: 'completedBy',
        title: '完成人',
        width: 120,
        cell: ({ row }) => row.completedBy || '-',
      },
      {
        colKey: 'completedAt',
        title: '完成时间',
        width: 180,
        cell: ({ row }) => (row.completedAt ? formatDateTime(row.completedAt) : '-'),
      },
      {
        colKey: 'operation',
        title: '操作',
        width: 220,
        fixed: 'right',
        cell: ({ row }) => (
          <Space size="small">
            <Button
              theme="primary"
              variant="text"
              icon={<BrowseIcon />}
              onClick={() => handleOpenDetail(row)}
            >
              详情
            </Button>
            {row.dispatchStatus === DispatchStatus.Dispatched && (
              <Popconfirm
                content={`确定重新下发该指令（ID ${row.id}）吗？将重新生成 token 并发送到对应子群。`}
                onConfirm={() => handleRedispatch(row)}
              >
                <Button theme="primary" variant="text" icon={<SendIcon />}>
                  再次下发
                </Button>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, page],
  );

  const handlePageChange = (info: PageInfo) => {
    setPage(info.current);
    setPageSize(info.pageSize);
  };

  return (
    <div className="command-list-page">
      <div className="command-list-toolbar">
        <div className="filters">
          <Select
            value={subCategory}
            onChange={(val) => {
              setSubCategory(val as number | undefined);
              setPage(1);
            }}
            options={[...SUB_CATEGORY_OPTIONS]}
            placeholder="指令分类"
            clearable
            style={{ width: 200 }}
          />
          <Button variant="outline" icon={<RefreshIcon />} onClick={handleResetFilters}>
            重置
          </Button>
        </div>
        <Button theme="primary" icon={<AddIcon />} onClick={handleOpenCreate}>
          新增指令
        </Button>
      </div>

      <Table
        rowKey="id"
        data={list}
        columns={columns}
        loading={loading}
        bordered
        stripe
        size="medium"
        pagination={{
          current: page,
          pageSize,
          total,
          showJumper: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: handlePageChange,
        }}
      />

      <CommandFormDialog
        visible={dialogVisible}
        loading={submitting}
        onCancel={() => setDialogVisible(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default CommandList;
